import React, { useEffect } from 'react';
import ReactDOM from 'react-dom/client';
import * as Sentry from '@sentry/react';
import { GrowthBook, GrowthBookProvider } from '@growthbook/growthbook-react';
import mixpanel from 'mixpanel-browser';

import { waitForRuntimeConfig } from '@v7/form/src/utils/runtimeConfig';
import { DEFAULT_SENTRY_NETWORK_URLS } from '@v7/form/src/utils/constant';
import { getGrowthbookInstance } from '@v7/form/src/utils/growthbook';

import './index.css';
import App from './App';
import reportWebVitals from './reportWebVitals';

const mixpanel_token = process.env.REACT_APP_MIXPANEL_TOKEN;
const build_env = process.env.REACT_APP_ENV;

// EU data-residency host; US forwarding (api.mixpanel.com) is being retired.
const MIXPANEL_EU_API_HOST = 'https://api-eu.mixpanel.com';

if (mixpanel_token && build_env !== 'development') {
  mixpanel.init(mixpanel_token, { api_host: MIXPANEL_EU_API_HOST, ignore_dnt: true });
}

const buildGraphqlUrl = (baseUrl: string): string =>
  baseUrl.endsWith('/v1/graphql') ? baseUrl : `${baseUrl}/v1/graphql`;

const initSentry = (networkDetailAllowUrls: string[]): void => {
  Sentry.init({
    dsn: 'https://741d762eaf8195ade0aa63e0ab62b948@sentry.talview.org/172',
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration({
        networkDetailAllowUrls,
      }),
    ],
    release: process.env.VERSION || 'dev',
    tracesSampleRate: process.env.NODE_ENV !== 'production' ? 1.0 : 0.5,
    environment: process.env.APP_ENV || process.env.NODE_ENV || 'internal',
    enabled: process.env.NODE_ENV !== 'development',
    replaysSessionSampleRate: process.env.NODE_ENV !== 'production' ? 1.0 : 0.5,
    replaysOnErrorSampleRate: 1.0,
    normalizeDepth: 10,
  });
};

let hasInitializedTelemetry = false;

const initializeTelemetry = async (): Promise<void> => {
  if (hasInitializedTelemetry) {
    return;
  }
  hasInitializedTelemetry = true;

  try {
    const config = await waitForRuntimeConfig();
    const { REACT_APP_MIXPANEL_TOKEN: mixpanelToken, REACT_APP_BASE_URL: runtimeBaseUrl } = config;

    if (mixpanelToken && build_env !== 'development') {
      mixpanel.init(mixpanelToken, { api_host: MIXPANEL_EU_API_HOST, ignore_dnt: true });
    }
    console.log('runtime', runtimeBaseUrl);

    const networkDetailAllowUrls = runtimeBaseUrl ? [buildGraphqlUrl(runtimeBaseUrl)] : DEFAULT_SENTRY_NETWORK_URLS;

    initSentry(networkDetailAllowUrls);
  } catch (error) {
    console.error('[Telemetry] Failed to initialize using runtime config', error);
    initSentry(DEFAULT_SENTRY_NETWORK_URLS);
  }
};

const initializeGrowthbook = async (): Promise<void> => {
  try {
    console.log('Initializing GrowthBook...');
    const growthbook = await getGrowthbookInstance();
    console.log('GrowthBook initialized');
    renderApp(growthbook);
  } catch (error) {
    console.error('[GrowthBook] Failed to initialize', error);
    renderApp(new GrowthBook());
  }
};

const TelemetryInitializer = (): null => {
  useEffect(() => {
    void initializeTelemetry();
    void initializeGrowthbook();
  }, []);

  return null;
};

const root = ReactDOM.createRoot(document.getElementById('root') as HTMLElement);

const renderApp = (growthbook?: GrowthBook): void => {
  root.render(
    <React.StrictMode>
      <TelemetryInitializer />
      {growthbook ? (
        <GrowthBookProvider growthbook={growthbook}>
          <App />
        </GrowthBookProvider>
      ) : (
        <App />
      )}
    </React.StrictMode>,
  );
};

renderApp();

//env update
// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();
